import React, { useCallback, useState } from 'react';
import { WhoIsWearingWithBundle } from 'types';
import tw from '../../tailwind.config';
import { useRouter } from 'next/router';
import { getWhoIsWearing } from 'services/whoIsWearing';
import { slugifySnake } from 'utils/utils';
import DoubleHeader from 'components/typography/DoubleHeader';
import Head from 'next/head';
import { WhoIsWearingSettings } from '../../sanity/sanity.types';
import { withPageStaticProps } from 'utils/header';

interface Props {
  items: WhoIsWearingWithBundle[];
  settings: WhoIsWearingSettings;
}

const WhoIsWearing = (props: Props) => {
  const router = useRouter();

  const pageTitle = props.settings.title ?? `Who's Wearing Generation Tux`;

  const navigateToPostPage = useCallback(
    (item: WhoIsWearingWithBundle) => {
      const nameAsSlug = slugifySnake(item.name ?? 'missing');

      router.push(`/whos-wearing-generation-tux/celebrity/${nameAsSlug}`);
    },
    [router]
  );

  return (
    <>
      <Head>
        {pageTitle && <title>{pageTitle}</title>}

        <meta name="description" key="description" content={pageTitle} />

        <meta property="og:url" key="og:url" content={`https://generationtux.com/whos-wearing-generation-tux`} />
        <meta property="og:type" key="og:type" content="website" />
        <meta property="og:title" key="og:title" content={pageTitle} />
        <meta property="og:description" key="og:description" content={pageTitle} />

        <meta name="twitter:card" key="twitter:card" content="summary_large_image" />
        <meta name="twitter:domain" key="twitter:domain" content="generationtux.com" />
        <meta name="twitter:url" key="twitter:url" content="https://generationtux.com/whos-wearing-generation-tux" />
        <meta name="twitter:title" key="twitter:title" content={pageTitle} />
        <meta name="twitter:description" key="twitter:description" content={pageTitle} />
      </Head>

      <div className="bg-gray-lighter py-1">
        <div className="m-section-hero">
          <div className="container">
            <div className="row">
              <div className="col-span-12">
                <DoubleHeader
                  hero
                  smallTitle={props.settings.smallTitle?.length ? props.settings.smallTitle : 'Inspiration'}
                  largeTitle={
                    props.settings.largeTitle?.length ? props.settings.largeTitle : "Who's Wearing Generation Tux"
                  }
                />
                <p className="my-8 text-gray-dark">
                  {props.settings.openingParagraph?.length
                    ? props.settings.openingParagraph
                    : 'From the playing field, to the red carpet, and all the way down the aisle, Generation Tux is everywhere these days.'}
                </p>
                <p>Get inspired with these high profile looks.</p>
              </div>
            </div>

            <div className="row mt-32 items-stretch sm:mt-64" id="scroll-to-here">
              <div className="col-span-12 sm:col-span-8 md:col-span-9">
                <div className="grid w-full grid-cols-2 gap-x-16 gap-y-32 md:grid-cols-3 lg:grid-cols-4">
                  {props.items.map((item: WhoIsWearingWithBundle) => (
                    <div
                      key={item._id}
                      id={`whos-wearing-item-${item._id}`}
                      className="block cursor-pointer"
                      onClick={() => navigateToPostPage(item)}
                    >
                      <div className="aspect-ratio-1by1">
                        <picture>
                          <source
                            media={`(min-width: ${tw.theme.screens['lg']})`}
                            srcSet={`${item.image?.asset.url}?fit=crop&w=276&h=276`}
                          />
                          <source
                            media={`(min-width: ${tw.theme.screens['sm']})`}
                            srcSet={`${item.image?.asset.url}?fit=crop&w=208&h=208`}
                          />
                          <img
                            className="transition-150 w-full filter transition hover:shadow-3 hover:brightness-[108%]"
                            src={`${item.image?.asset.url}?fit=crop&w=382&h=382`}
                            alt={`${item.bundle.display_name}`}
                            loading="lazy"
                          />
                        </picture>
                      </div>

                      <p className="white text-sm mt-8 overflow-hidden overflow-ellipsis whitespace-nowrap text-gray-dark">
                        {item.name ?? ''}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export const getStaticProps = withPageStaticProps(async () => {
  const { allWhoIsWearing: items, allWhoIsWearingSettings } = await getWhoIsWearing();

  return {
    props: {
      items,
      settings: allWhoIsWearingSettings,
    },
  };
});

export default WhoIsWearing;
